import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { notification } from 'antd';

const UnresponsiveCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [newCustomer, setNewCustomer] = useState({
    phone: '',
    notes: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) throw new Error('User not authenticated');
      const shopId = window.localStorage.getItem('activeShopId');
      if (!shopId) throw new Error('Aucune boutique active');

      const { data, error } = await supabase
        .from('unresponsive_customers')
        .select('*')
        .eq('user_id', user.id)
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers(data);
    } catch (error) {
      notification.error({
        message: 'Erreur',
        description: 'Erreur lors du chargement des clients'
      });
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) throw new Error('User not authenticated');
      const shopId = window.localStorage.getItem('activeShopId');
      if (!shopId) throw new Error('Aucune boutique active');

      const { data, error } = await supabase
        .from('unresponsive_customers')
        .insert([{
          phone: newCustomer.phone,
          notes: newCustomer.notes,
          user_id: user.id,
          shop_id: shopId
        }]);

      if (error) throw error;

      notification.success({
        message: 'Succès',
        description: 'Client ajouté avec succès'
      });
      setNewCustomer({ phone: '', notes: '' });
      fetchCustomers();
    } catch (error) {
      notification.error({
        message: 'Erreur',
        description: 'Erreur lors de l\'ajout du client'
      });
      console.error('Error:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const shopId = window.localStorage.getItem('activeShopId');
      if (!shopId) throw new Error('Aucune boutique active');
      const { error } = await supabase
        .from('unresponsive_customers')
        .delete()
        .eq('id', id)
        .eq('shop_id', shopId);

      if (error) throw error;

      notification.success({
        message: 'Succès',
        description: 'Client supprimé avec succès'
      });
      fetchCustomers();
    } catch (error) {
      notification.error({
        message: 'Erreur',
        description: 'Erreur lors de la suppression'
      });
      console.error('Error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold mb-6">Clients Non Répondants</h1>

      {/* Formulaire d'ajout */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Ajouter un nouveau client</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Numéro de téléphone</label>
            <input
              type="tel"
              value={newCustomer.phone}
              onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              value={newCustomer.notes}
              onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              rows="3"
            />
          </div>
          <button
            type="submit"
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Ajouter
          </button>
        </form>
      </div>

      {/* Liste des clients */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Sous md : chaque client devient une carte (thead masqué) ; dès md : tableau classique */}
        <table className="block min-w-full divide-y divide-gray-200 md:table">
          <thead className="hidden bg-gray-50 md:table-header-group">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Téléphone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Notes
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date d'ajout
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="block bg-white divide-y divide-gray-200 md:table-row-group">
            {customers.map((customer) => (
              <tr key={customer.id} className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 p-4 md:table-row md:p-0">
                <td className="order-1 font-medium md:table-cell md:px-6 md:py-4 md:whitespace-nowrap md:font-normal">{customer.phone}</td>
                <td className="order-3 col-span-2 break-words text-sm text-gray-700 empty:hidden md:table-cell md:px-6 md:py-4 md:text-base md:text-gray-900">{customer.notes}</td>
                <td className="order-4 col-span-2 text-xs text-gray-500 md:table-cell md:px-6 md:py-4 md:whitespace-nowrap md:text-base md:text-gray-900">
                  {/* Libellé visible uniquement en mode carte (mobile) */}
                  <span className="md:hidden">Ajouté le </span>
                  {new Date(customer.created_at).toLocaleDateString()}
                </td>
                <td className="order-2 md:table-cell md:px-6 md:py-4 md:whitespace-nowrap">
                  <button
                    onClick={() => handleDelete(customer.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UnresponsiveCustomers;
